import React, { useState } from 'react'
import { ChevronLeftOutlined, ChevronRightOutlined, LockOutlined } from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

const EM_DASH = '—'

/** Storage position label `SH<shelf>·R<row>·C<column>`, omitting empty segments. */
function positionLabel (location) {
  const segments = []
  if (location?.shelf !== null && location?.shelf !== undefined && location?.shelf !== '') segments.push(`SH${location.shelf}`)
  if (location?.row !== null && location?.row !== undefined && location?.row !== '') segments.push(`R${location.row}`)
  if (location?.column !== null && location?.column !== undefined && location?.column !== '') segments.push(`C${location.column}`)
  return segments.join('·')
}

/**
 * MachineResourcesList — the resources currently on a device (from `fixtureLocations`),
 * as a FRONTEND-paginated list (story 35, PO): a storage can hold 100+ resources, so we
 * show one page at a time with prev/next instead of rendering them all (which would
 * overflow the card/detail). "Resources", not "Parts" — the contents are not always
 * parts (fixtures, raw material, …). Presentation only; data comes from the screen tick.
 *
 * `testIdPrefix` keeps the existing per-row test ids (`<prefix>-<absoluteIndex>`).
 */
export default function MachineResourcesList ({ locations = [], testIdPrefix, pageSize = 6, style }) {
  const { t_ } = useSmartTranslation()
  const [page, setPage] = useState(0)

  const pageCount = Math.max(1, Math.ceil(locations.length / pageSize))
  const current = Math.min(page, pageCount - 1)
  const start = current * pageSize
  const pageItems = locations.slice(start, start + pageSize)

  return (
    <div className='mcx-prodparts' style={style}>
      <span className='mcx-prodparts__title'>{t_('Resources on machine')} ({locations.length})</span>
      {pageItems.map((location, index) => {
        const resource = location.resources?.[0]
        return (
          <div key={resource?.id ?? (start + index)} className='mcx-partline' data-testid={`${testIdPrefix}-${start + index}`}>
            <span className='mcx-partline__code'>{resource?.code ?? EM_DASH}</span>
            {resource?.locked && <LockOutlined className='mcx-partline__lock' sx={{ fontSize: 14 }} />}
            <span className='mcx-partline__pos'>{positionLabel(location)}</span>
          </div>
        )
      })}
      {locations.length === 0 && <span className='mcx-partline__pos'>{EM_DASH}</span>}
      {pageCount > 1 && (
        <div className='mcx-partpager'>
          <button
            type='button'
            className='mcx-partpager__btn'
            data-testid={`${testIdPrefix}-prev`}
            aria-label={t_('Previous')}
            disabled={current === 0}
            onClick={() => setPage(current - 1)}
          >
            <ChevronLeftOutlined sx={{ fontSize: 18 }} />
          </button>
          <span className='mcx-partpager__label'>{start + 1}–{Math.min(start + pageSize, locations.length)} / {locations.length}</span>
          <button
            type='button'
            className='mcx-partpager__btn'
            data-testid={`${testIdPrefix}-next`}
            aria-label={t_('Next')}
            disabled={current >= pageCount - 1}
            onClick={() => setPage(current + 1)}
          >
            <ChevronRightOutlined sx={{ fontSize: 18 }} />
          </button>
        </div>
      )}
    </div>
  )
}
